import { useMemo } from 'react';
import { clsx } from 'clsx';
import { HorizontalStack } from '../alignment/horizontal-stack';
import type { TypographyProps } from '../typography';
import { type Breadcrumb, BreadcrumbComponent } from './breadcrumb';
import styles from './breadcrumbs.module.scss';

export type { Breadcrumb };

const baseTypographyProps: TypographyProps = {
  size: '0.875rem',
  lineHeight: '1.5rem',
  inheritance: 'current',
  wrap: 'no-wrap',
};

export const inactiveBreadcrumbTypographyProps: TypographyProps = {
  ...baseTypographyProps,
  tone: 'neutral-dk',
};

const activeBreadcrumbTypographyProps: TypographyProps = {
  ...baseTypographyProps,
  tone: 'md',
  wrap: 'ellipsis',
};

interface BreadcrumbsProps {
  breadcrumbs: Breadcrumb[];
  className?: string;
  lineHeight?: TypographyProps['lineHeight'];
  withPadding?: boolean;
}

export function Breadcrumbs({ breadcrumbs, className, lineHeight, withPadding = true }: BreadcrumbsProps) {
  const finalInactiveBreadcrumbTypographyProps = useMemo(() => {
    if (!lineHeight) {
      return inactiveBreadcrumbTypographyProps;
    }

    return { ...inactiveBreadcrumbTypographyProps, lineHeight };
  }, [lineHeight]);

  const finalActiveTypographyProps = useMemo(() => {
    if (!lineHeight) {
      return activeBreadcrumbTypographyProps;
    }

    return { ...activeBreadcrumbTypographyProps, lineHeight };
  }, [lineHeight]);

  return (
    <HorizontalStack blockAlign="center" className={clsx(styles.breadcrumbs, withPadding && styles.withPadding, className)} wrap="nowrap">
      {breadcrumbs.map((breadcrumb, i) => {
        const isLastCrumb = i === breadcrumbs.length - 1;
        const crumbTypography = isLastCrumb ? finalActiveTypographyProps : finalInactiveBreadcrumbTypographyProps;

        return (
          <BreadcrumbComponent
            key={breadcrumb.id}
            {...breadcrumb}
            className={clsx(isLastCrumb ? (i === 0 ? undefined : styles.padLeft) : styles.padRight, breadcrumb.className)}
            isLastCrumb={isLastCrumb}
            typography={crumbTypography}
          />
        );
      })}
    </HorizontalStack>
  );
}
